import { z } from 'zod';

import type { PutApiV1SerieIdMutationRequest } from '@/shared/api/generated';

/**
 * Turma vinculada à série, exibida na seção "Turmas" do formulário.
 *
 * Mantida em estado local do formulário: adicionar/remover uma turma aqui NÃO
 * chama nenhuma API — a lista completa só é persistida (como `classStudentId[]`)
 * quando o formulário inteiro da Série é salvo. Não existe endpoint de vínculo
 * incremental turma↔série.
 */
export interface SerieClassStudentFormValue {
  readonly classStudentId: number;
  /** Descrição de exibição da turma (não é enviada ao salvar — só `classStudentId`). */
  readonly description: string;
}

/**
 * Schema do formulário de Série (criar/editar). Validado por Zod (React Hook Form
 * + resolver).
 *
 * NÃO derivado de `serieViewModelSchema` (gerado pelo Kubb): aquele schema é
 * exportado com o cast `as unknown as ToZod<SerieViewModel>`, o que torna a shape
 * do `ZodObject` uma ficção de tipo — qualquer `.pick()`/`.extend()` sobre ele
 * degrada o `z.infer` para `never` (mesmo problema documentado em
 * `location/types/countryForm.ts`). Por isso o schema é definido aqui
 * diretamente. Note também que o contrato de **escrita** (`PostApiV1SerieMutationRequest`/
 * `PutApiV1SerieIdMutationRequest`, multipart/form-data) usa PascalCase e não é o
 * mesmo shape do `SerieViewModel` (leitura, camelCase) — o mapeamento entre os
 * dois vive em `useSaveSerie`.
 */
export const serieFormSchema = z.object({
  /** ID da empresa associada; `undefined` quando geral (do sistema) ou não escolhida. */
  companyId: z.number().optional(),
  title: z.string().trim().min(1),
  description: z.string().trim().min(1),
  general: z.boolean(),
  active: z.boolean(),
  /** Novo arquivo de miniatura escolhido pelo operador; `null` = sem alteração. */
  thumbnailFile: z.instanceof(File).nullable(),
  /** Marca a miniatura existente para remoção no próximo salvamento (só edição). */
  removeThumbnail: z.boolean(),
  classStudents: z
    .object({
      classStudentId: z.number(),
      description: z.string(),
    })
    .array(),
});

/**
 * Valores do formulário de Série (create/edit).
 *
 * O tipo intersecta com uma asserção nula (no-op) que só permanece `unknown`
 * enquanto os campos usados por `useSaveSerie` continuarem existindo em
 * `PutApiV1SerieIdMutationRequest` — se um for renomeado/removido no contrato
 * gerado pelo Kubb, a asserção vira `never` e o `yarn type-check` quebra aqui.
 */
export type SerieFormValues = z.infer<typeof serieFormSchema> &
  (
    | 'CompanyId'
    | 'Title'
    | 'Resumee'
    | 'Active'
    | 'IsGeneral'
    | 'Thumbnail'
    | 'RemoveThumbnail'
    | 'ClassStudentIds' extends keyof PutApiV1SerieIdMutationRequest
    ? unknown
    : never);
